import { useQuery } from '@tanstack/react-query';
import {
  fetchRangeSafetyEvents,
  sortSafetyEventsByTimestamp,
  type RangeSafetyEventsParams,
  type SafetyEvent,
} from '@apis/reports';

interface Args {
  siteId: string;
  startTimestamp: number;
  endTimestamp: number;
  areaId?: string;
  floorId?: string;
  /** Caller-supplied gate. Same role as `enabled` in `useQuery` — the
   *  hook adds its own non-empty checks on top. */
  enabled?: boolean;
}

/**
 * Reads safety events for a given time range, optionally scoped to a
 * specific area or floor. The Incident Report uses two instances of this
 * hook: one for the subject-wide top summary card (no area/floor filter,
 * full `at ± 30 min` window) and one per selected stop (area-scoped, stop
 * arrival → departure window).
 *
 * Cache key includes every filter so the two instances don't shadow each
 * other.
 */
export function useRangeSafetyEventsQuery(
  args: Args,
): ReturnType<typeof useQuery<SafetyEvent[]>> {
  const params: RangeSafetyEventsParams = {
    siteId: args.siteId,
    startTimestamp: args.startTimestamp,
    endTimestamp: args.endTimestamp,
    ...(args.areaId ? { areaId: args.areaId } : {}),
    ...(args.floorId ? { floorId: args.floorId } : {}),
  };
  return useQuery({
    queryKey: [
      'reports',
      'incident-report',
      'safety-events',
      args.siteId,
      args.startTimestamp,
      args.endTimestamp,
      args.areaId ?? null,
      args.floorId ?? null,
    ],
    queryFn: ({ signal }) => fetchRangeSafetyEvents(params, signal),
    // `select` runs once per fetch and memoises by reference — chronological
    // ordering reaches every consumer (chip grid, per-stop list) for free.
    select: sortSafetyEventsByTimestamp,
    enabled:
      !!args.siteId &&
      args.startTimestamp > 0 &&
      args.endTimestamp > 0 &&
      (args.enabled ?? true),
    staleTime: 60 * 1000,
  });
}
